import type { OpenAiUsageBucket, OpenAiUsagePage } from './types';
import type { ApiResponse } from './types';
import { apiRequest } from './client';
import { debugLog } from '../debug-log';

export const logOpenAIUsageRequest = (category: string, params: URLSearchParams) => {
  try {
    const snapshot: Record<string, string | string[]> = {};
    for (const [key, value] of params.entries()) {
      const existing = snapshot[key];
      if (existing === undefined) {
        snapshot[key] = value;
        continue;
      }
      snapshot[key] = Array.isArray(existing) ? [...existing, value] : [existing, value];
    }
    debugLog(`[OpenAI Usage] Request → ${category}`, snapshot);
  } catch (error) {
    console.warn('[OpenAI Usage] Failed to serialize request params', error);
  }
};

export const OPENAI_USAGE_LIMIT_BY_BUCKET: Record<string, number> = {
  '1m': 1440,
  '1h': 168,
  '1d': 31
};

const getOpenAiUsageLimit = (bucketWidth: string): string => {
  return (OPENAI_USAGE_LIMIT_BY_BUCKET[bucketWidth] ?? OPENAI_USAGE_LIMIT_BY_BUCKET['1d']).toString();
};

export const buildOpenAiUsageParams = (
  startTime: number,
  endTime: number | undefined,
  bucketWidth: string,
  groupBy: string[]
): URLSearchParams => {
  const params = new URLSearchParams({
    start_time: startTime.toString(),
    limit: getOpenAiUsageLimit(bucketWidth),
    bucket_width: bucketWidth
  });

  if (endTime) {
    params.append('end_time', endTime.toString());
  }

  groupBy.forEach(group => params.append('group_by', group));
  return params;
};

// Usage/costs API はカーソルページネーション（has_more + next_page → page）。
// group_by の組み合わせ次第で 1 リクエストに収まらないため、必ず全ページ追跡する。
const MAX_USAGE_PAGES = 20;

/**
 * 複数ページに分かれた bucket リストを start_time でマージする。
 * 同一 bucket が複数ページに分割された場合は results を連結する。
 */
export const mergeOpenAiUsageBucketPages = (pages: OpenAiUsageBucket[][]): OpenAiUsageBucket[] => {
  const bucketsByStart = new Map<number, OpenAiUsageBucket>();
  for (const buckets of pages) {
    for (const bucket of buckets) {
      const start = Number(bucket?.start_time);
      if (!Number.isFinite(start)) continue;
      const existing = bucketsByStart.get(start);
      if (existing) {
        existing.results = [...(existing.results ?? []), ...(bucket?.results ?? [])];
      } else {
        bucketsByStart.set(start, { ...bucket, results: [...(bucket?.results ?? [])] });
      }
    }
  }
  return Array.from(bucketsByStart.values())
    .sort((a, b) => Number(a.start_time) - Number(b.start_time));
};

const fetchAllOpenAiUsagePages = async (
  apiKey: string,
  category: string,
  baseParams: URLSearchParams
): Promise<ApiResponse<OpenAiUsagePage>> => {
  const pages: OpenAiUsageBucket[][] = [];
  let page: string | undefined;
  const seenPages = new Set<string>();
  let rateLimits: ApiResponse<OpenAiUsagePage>['rateLimits'];

  for (let i = 0; i < MAX_USAGE_PAGES; i += 1) {
    const params = new URLSearchParams(baseParams);
    if (page) params.append('page', page);
    logOpenAIUsageRequest(category, params);

    const res = await apiRequest<OpenAiUsagePage>(`/usage/${category}?${params}`, apiKey);
    if (!res.success) {
      return res;
    }
    rateLimits = res.rateLimits ?? rateLimits;
    if (!Array.isArray(res.data?.data)) return { success: false, error: 'Invalid usage page data' };
    pages.push(res.data.data);

    const nextPage: string | undefined = res.data?.has_more ? res.data?.next_page : undefined;
    if (!res.data.has_more) {
      break;
    }
    if (!nextPage || seenPages.has(nextPage) || i === MAX_USAGE_PAGES - 1) {
      return { success: false, error: 'Incomplete usage pagination; response was not cached' };
    }
    seenPages.add(nextPage);
    page = nextPage;
  }

  return {
    success: true,
    data: { object: 'page', data: mergeOpenAiUsageBucketPages(pages), has_more: false, next_page: null },
    rateLimits
  };
};

export const getOrganizationCosts = async (
  apiKey: string,
  startTime: number,
  endTime: number,
  options?: {
    limit?: number;
    page?: string;
    groupBy?: string[];
  }
): Promise<ApiResponse<OpenAiUsagePage>> => {
  const DAY_SECONDS = 24 * 60 * 60;
  const spanDays = Math.max(1, Math.ceil((endTime - startTime) / DAY_SECONDS));
  const limit = Math.min(180, options?.limit ?? spanDays);

  const baseParams = new URLSearchParams({
    start_time: startTime.toString(),
    end_time: endTime.toString(),
    bucket_width: '1d',
    limit: limit.toString()
  });

  options?.groupBy?.forEach(group => baseParams.append('group_by', group));

  const pages: OpenAiUsageBucket[][] = [];
  let page: string | undefined = options?.page;
  const seenPages = new Set<string>(page ? [page] : []);
  let rateLimits: ApiResponse<OpenAiUsagePage>['rateLimits'];

  for (let i = 0; i < MAX_USAGE_PAGES; i += 1) {
    const params = new URLSearchParams(baseParams);
    if (page) params.append('page', page);
    logOpenAIUsageRequest('costs', params);

    const res = await apiRequest<OpenAiUsagePage>(`/costs?${params}`, apiKey);
    if (!res.success) {
      return res;
    }
    rateLimits = res.rateLimits ?? rateLimits;
    if (!Array.isArray(res.data?.data)) return { success: false, error: 'Invalid cost page data' };
    pages.push(res.data.data);

    const nextPage: string | undefined = res.data?.has_more ? res.data?.next_page : undefined;
    if (!res.data.has_more) {
      break;
    }
    if (!nextPage || seenPages.has(nextPage) || i === MAX_USAGE_PAGES - 1) {
      return { success: false, error: 'Incomplete cost pagination; response was not cached' };
    }
    seenPages.add(nextPage);
    page = nextPage;
  }

  return {
    success: true,
    data: { object: 'page', data: mergeOpenAiUsageBucketPages(pages), has_more: false, next_page: null },
    rateLimits
  };
};

/**
 * 新しいUsage API: Completions使用状況を取得
 */
export const getCompletionsUsage = async (
  apiKey: string,
  startTime: number,
  endTime?: number,
  bucketWidth: string = '1d'
): Promise<ApiResponse<OpenAiUsagePage>> => {
  const params = buildOpenAiUsageParams(startTime, endTime, bucketWidth, [
    'api_key_id',
    'model',
    'project_id',
    'service_tier',
    'batch'
  ]);

  return fetchAllOpenAiUsagePages(apiKey, 'completions', params);
};

/**
 * 新しいUsage API: Embeddings使用状況を取得
 */
export const getEmbeddingsUsage = async (
  apiKey: string,
  startTime: number,
  endTime?: number,
  bucketWidth: string = '1d'
): Promise<ApiResponse<OpenAiUsagePage>> => {
  // 注意: embeddings の group_by は project_id / user_id / api_key_id / model のみ対応
  // （batch は completions 専用。指定すると 400 になりうる）
  const params = buildOpenAiUsageParams(startTime, endTime, bucketWidth, [
    'api_key_id',
    'model',
    'project_id'
  ]);

  return fetchAllOpenAiUsagePages(apiKey, 'embeddings', params);
};

/**
 * 新しいUsage API: Moderations使用状況を取得
 */
export const getModeratioUsage = async (
  apiKey: string,
  startTime: number,
  endTime?: number,
  bucketWidth: string = '1d'
): Promise<ApiResponse<OpenAiUsagePage>> => {
  const params = buildOpenAiUsageParams(startTime, endTime, bucketWidth, [
    'api_key_id',
    'model',
    'project_id'
  ]);
  return fetchAllOpenAiUsagePages(apiKey, 'moderations', params);
};

/**
 * 新しいUsage API: Images使用状況を取得
 */
export const getImagesUsage = async (
  apiKey: string,
  startTime: number,
  endTime?: number,
  bucketWidth: string = '1d'
): Promise<ApiResponse<OpenAiUsagePage>> => {
  const params = buildOpenAiUsageParams(startTime, endTime, bucketWidth, [
    'api_key_id',
    'model',
    'project_id'
  ]);
  return fetchAllOpenAiUsagePages(apiKey, 'images', params);
};

/**
 * 新しいUsage API: Audio Speeches使用状況を取得
 */
export const getAudioSpeechesUsage = async (
  apiKey: string,
  startTime: number,
  endTime?: number,
  bucketWidth: string = '1d'
): Promise<ApiResponse<OpenAiUsagePage>> => {
  const params = buildOpenAiUsageParams(startTime, endTime, bucketWidth, [
    'api_key_id',
    'model',
    'project_id'
  ]);
  return fetchAllOpenAiUsagePages(apiKey, 'audio_speeches', params);
};

/**
 * 新しいUsage API: Audio Transcriptions使用状況を取得
 */
export const getAudioTranscriptionsUsage = async (
  apiKey: string,
  startTime: number,
  endTime?: number,
  bucketWidth: string = '1d'
): Promise<ApiResponse<OpenAiUsagePage>> => {
  const params = buildOpenAiUsageParams(startTime, endTime, bucketWidth, [
    'api_key_id',
    'model',
    'project_id'
  ]);
  return fetchAllOpenAiUsagePages(apiKey, 'audio_transcriptions', params);
};
